/**
 * Card art of Football 101 (one picture per card beat) and the lesson-7 mini counter square. Built from the
 * painted heads/gloves of the asset manifest plus broadcast-style diagrams (no stock art, no emoji).
 */
import { Fragment } from 'preact';
import type { CharacterId } from '@hf/shared';
import { Head } from '../components';
import type { SquareCell } from '@hf/client-core/ui-model/tutorial/catalog';
import { fakeCost, MORE_FAKES } from '@hf/client-core/ui-model/tutorial/fakes';
import { FakeKeys, useTutorialDevice } from './keys';

const OPP: Record<CharacterId, CharacterId> = {
  rookie: 'bigtex',
  bigtex: 'grandpa',
  zorg: 'bigtex',
  grandpa: 'bigtex',
  turbo: 'bigtex',
  dome: 'bigtex',
  rumble: 'bigtex',
  hopper: 'bigtex',
  lodestone: 'bigtex',
};

export function CardArt({ kind, head }: { kind: string; head: CharacterId }) {
  switch (kind) {
    case 'downs':
      return (
        <div class="hf-cardart downs" aria-label="Four tries to go ten yards">
          <div class="hf-downs">
            <div class="hf-downs__field">
              <span class="hf-downs__los" />
              <span class="hf-downs__ltg" />
              <span class="hf-downs__arrow t-broadcast">10 YARDS</span>
              <Head character={head} expression="determined" class="hf-downs__head" />
            </div>
            <div class="hf-downs__tries">
              {[1, 2, 3, 4].map((n) => (
                <span key={n} class={`hf-downs__try t-broadcast ${n === 4 ? 'last' : ''}`}>
                  <b class="t-display">{n}</b>
                  {n === 1 ? 'ST' : n === 2 ? 'ND' : n === 3 ? 'RD' : 'TH'}
                </span>
              ))}
            </div>
          </div>
          <div class="hf-cardart__cap t-broadcast">REACH THE YELLOW LINE = 4 NEW TRIES</div>
        </div>
      );
    case 'turnover':
      return (
        <div class="hf-cardart turnover" aria-label="Turnover on downs">
          <div class="hf-turn">
            <Head character={head} expression="shocked" class="hf-turn__head" />
            <span class="hf-turn__ball t-display">→</span>
            <Head character={OPP[head]} expression="celebrating" flip class="hf-turn__head" />
          </div>
          <div class="hf-cardart__cap t-broadcast">4 TRIES · NO FIRST DOWN · THEIR BALL</div>
        </div>
      );
    case 'square':
      return (
        <div class="hf-cardart square" aria-label="The counter square">
          <BigSquare head={head} />
        </div>
      );
    case 'scoring':
      return (
        <div class="hf-cardart scoring" aria-label="Scoring">
          <div class="hf-pts">
            <span class="hf-pts__chip gold">
              <b class="t-display">6</b>
              <small class="t-broadcast">TOUCHDOWN</small>
            </span>
            <span class="hf-pts__chip">
              <b class="t-display">+1</b>
              <small class="t-broadcast">TOSS</small>
            </span>
            <span class="hf-pts__chip">
              <b class="t-display">+2</b>
              <small class="t-broadcast">RUN IT IN</small>
            </span>
            <span class="hf-pts__chip blue">
              <b class="t-display">3</b>
              <small class="t-broadcast">FIELD GOAL</small>
            </span>
          </div>
        </div>
      );
    case 'morefakes':
      return <MoreFakes />;
    default:
      return null;
  }
}

/** Lesson 8's last card: the fakes the camp does not drill, with their keys (the same table as HOW TO PLAY). */
function MoreFakes() {
  const device = useTutorialDevice();
  return (
    <div class="hf-cardart morefakes" aria-label="Four more fakes">
      <div class="hf-more">
        {MORE_FAKES.map((f) => (
          <div class="hf-more__row" key={f.name}>
            <b class="t-display">{f.name}</b>
            <span class="hf-more__keys">
              <FakeKeys f={f} device={device} />
            </span>
            <small>Baits {f.baits}.</small>
            <i class="t-broadcast">
              {f.side === 'defense' ? 'DEFENSE' : 'CARRIER'} · {fakeCost(f)}
            </i>
          </div>
        ))}
      </div>
      <div class="hf-cardart__cap t-broadcast">TRY THEM IN PRACTICE · HOW TO PLAY › FAKES</div>
    </div>
  );
}

const CELLS: { id: SquareCell; carrier: string; defender: string; win: 'carrier' | 'defender' }[] = [
  { id: 'hurdle-dive', carrier: 'HURDLE', defender: 'DIVE', win: 'carrier' },
  { id: 'leap-hurdle', carrier: 'HURDLE', defender: 'LEAP', win: 'defender' },
  { id: 'dive-truck', carrier: 'TRUCK', defender: 'DIVE', win: 'defender' },
  { id: 'truck-leap', carrier: 'TRUCK', defender: 'LEAP', win: 'carrier' },
];

/** The full square (card beat): rows = carrier moves, columns = defender moves. */
function BigSquare({ head }: { head: CharacterId }) {
  return (
    <div class="hf-sq">
      <span class="hf-sq__corner t-broadcast">
        CARRIER ↓<br />
        DEFENDER →
      </span>
      <span class="hf-sq__col t-broadcast">⬇ DIVE</span>
      <span class="hf-sq__col t-broadcast">⬆ LEAP</span>
      {['HURDLE', 'TRUCK'].map((row) => (
        <Fragment key={row}>
          <span class="hf-sq__row t-broadcast">{row}</span>
          {CELLS.filter((c) => c.carrier === row)
            .sort((a, b) => (a.defender === 'DIVE' ? -1 : 1) - (b.defender === 'DIVE' ? -1 : 1))
            .map((c) => (
              <span key={c.id} class={`hf-sq__cell ${c.win}`}>
                {c.win === 'carrier' ? (
                  <Head character={head} expression="celebrating" class="hf-sq__head" />
                ) : (
                  <Head character={OPP[head]} expression="celebrating" flip class="hf-sq__head" />
                )}
                <b class="t-broadcast">{c.win === 'carrier' ? `${c.carrier} WINS` : `${c.defender} WINS`}</b>
              </span>
            ))}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Lesson 7's square tracker (top-left, where the meters would be): the 2×2 square fills cell by cell as the reads
 * are proven, so the player watches the whole rule build up.
 */
export function MiniSquare({ cells }: { cells: readonly SquareCell[]; head?: CharacterId }) {
  const cell = (id: SquareCell) => {
    const c = CELLS.find((x) => x.id === id)!;
    const on = cells.includes(id);
    return (
      <span key={id} class={`hf-minisq__cell ${on ? `on ${c.win}` : ''}`}>
        {on ? (
          <b class="t-broadcast">{c.win === 'carrier' ? c.carrier : c.defender} WINS</b>
        ) : (
          <i class="t-display">?</i>
        )}
      </span>
    );
  };
  return (
    <div class="hf-minisq" aria-label={`Counter square: ${cells.length} of 4`}>
      <span class="hf-minisq__title t-display">THE COUNTER SQUARE</span>
      <span class="hf-minisq__corner" />
      <span class="hf-minisq__col t-broadcast">⬇ DIVE</span>
      <span class="hf-minisq__col t-broadcast">⬆ LEAP</span>
      <span class="hf-minisq__row t-broadcast">HURDLE</span>
      {cell('hurdle-dive')}
      {cell('leap-hurdle')}
      <span class="hf-minisq__row t-broadcast">TRUCK</span>
      {cell('dive-truck')}
      {cell('truck-leap')}
    </div>
  );
}
